import React, { useState, useEffect, useContext } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';
import { CORE_STAFF_ROLES } from '../config/roleConfig';
import guestRequestApi from '../services/guestRequestApi';
import userApi from '../services/userApi';
import { formatDate } from '../utils/dateFormatter';
import useRelocations from '../hooks/useRelocations';
import RelocationBanner from './RelocationBanner';
import { RELOCATION_UNKNOWN_AR } from '../config/relocationLabels';
import '../styles/GuestRequestModal.css';

// Re-usable across BookingDetails (Phase 3) and GuestDetailsPage (Phase 5).
// Phase 5 difference: parent picks a booking from a selector and passes
// the chosen booking object via the `booking` prop — modal logic unchanged.
//
// Required props:
//   show        — boolean, modal visibility
//   onHide      — () => void, close handler
//   booking     — populated booking object: _id, status, guestName, property{name}, checkIn, checkOut
//   onCreated   — (decoratedRequest) => void; receives backend-decorated record
//                 (displayPropertyName, displayGuestName, linkedTaskTicketId, linkedTaskStatus)

const TYPE_OPTIONS = [
  { value: 'cleaning', label: 'تنظيف' },
  { value: 'equipment', label: 'تجهيزات' },
  { value: 'transport', label: 'نقل' },
  { value: 'food', label: 'طعام' },
  { value: 'early_checkin', label: 'تسجيل وصول مبكر' },
  { value: 'late_checkout', label: 'تأخير المغادرة' },
  { value: 'other', label: 'طلب آخر' }
];

const PRIORITY_OPTIONS = [
  { value: 'normal', label: 'عادية' },
  { value: 'urgent', label: 'عاجلة' }
];

const FULFILLMENT_OPTIONS = [
  { value: 'internal', label: 'داخلي (فريقنا)' },
  { value: 'external', label: 'خارجي (طرف ثالث)' }
];

const initialFormState = {
  type: '',
  description: '',
  requestedForDate: '',  // v1.1.16-D: optional intake-layer guest timing intent (YYYY-MM-DD)
  priority: 'normal',
  fulfillmentType: 'internal',
  requiresFieldTask: false,
  assignedTo: '',
  taskAssignedTo: ''  // Phase 3.7a: User ObjectId for the auto-Task; only set when fulfillmentType=internal AND requiresFieldTask=true
};

const GuestRequestModal = ({ show, onHide, booking, onCreated }) => {
  const [formData, setFormData] = useState(initialFormState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // In-modal success state — shows the green confirmation with the new
  // Task's ticketId for ~1.5s before the modal closes. Only set when the
  // backend response indicates an auto-Task was actually created (gated on
  // result.request.linkedTaskTicketId). When ops files a GR-only request
  // without requiresFieldTask, modal closes immediately as before.
  const [successTicketId, setSuccessTicketId] = useState(null);

  // Phase 3.7a — staff users for the auto-Task assignment dropdown.
  // Lazy-loaded only when the conditional dropdown is needed.
  const [staffUsers, setStaffUsers] = useState([]);
  const [staffLoaded, setStaffLoaded] = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(false);

  // ── Where the guest actually IS ───────────────────────────────────────────────────────────────
  // The context block above describes the BOOKING, and the booking keeps naming the unit that was
  // sold for the whole stay — by design; the booking is never reassigned. But the work order this
  // form creates is raised against the unit the guest is currently in, so without this the operator
  // reads one apartment while dispatching a human to another. Verified in the dev run 2026-08-09:
  // a request raised from a modal headed «Dev-Test 25 Mar 2026» correctly produced a task on
  // P-0005, with nothing on screen saying so.
  //
  // The guest-request record itself stores NO unit — only the booking and the task — so there is
  // nothing to read but the move, and the batch hook is what already knows that a failed lookup
  // must never render as "nobody has moved".
  //
  // Gated on `show` (a closed modal asks nothing) AND on the role: unlike the arrivals card, whose
  // page is RoleGuard-ed, this modal hangs off `/bookings/:id`, which carries only an `AuthGuard`.
  // The relocation endpoint is core-staff-only, so for an owner it would 403 — turning the honest
  // "we could not check" warning into a permanent alarm for someone who cannot act on it.
  const { user } = useContext(AuthContext) || {};
  const mayAskWhereGuestIs = CORE_STAFF_ROLES.includes(user?.role);
  const { relocations, failed: relocationFailed } = useRelocations(
    [booking?._id],
    Boolean(show) && mayAskWhereGuestIs
  );
  const relocation = relocations[String(booking?._id)];

  // Reset form whenever the modal opens. Avoids leaking previous form state
  // across openings (especially relevant in Phase 5 where booking changes per open).
  useEffect(() => {
    if (show) {
      setFormData(initialFormState);
      setError(null);
      setLoading(false);
      setSuccessTicketId(null);
    }
  }, [show]);

  // Phase 3.7a — show the auto-Task assignment dropdown only when both
  // fulfillment is internal AND a field task is requested. External flows
  // and non-field-task flows don't surface it (KISS, scope-bounded).
  const showTaskAssignmentDropdown =
    formData.fulfillmentType === 'internal' && formData.requiresFieldTask === true;

  // Phase 3.7a — lazy-load staff users on first reveal of the dropdown.
  // Avoids unnecessary API call when ops chooses external or doesn't need a field task.
  //
  // IMPORTANT: `loadingStaff` is intentionally excluded from this effect's deps
  // and from its early-return guard. This effect calls `setLoadingStaff(true)`
  // itself; if `loadingStaff` were a dep, that internal write would re-trigger
  // the effect's cleanup mid-fetch (`cancelled = true`) and silently kill the
  // in-flight request — leaving the dropdown stuck on "loading..." forever.
  // Fetch-lifecycle gating belongs to `staffLoaded` only. `loadingStaff` is a
  // pure UI signal (spinner text + disabled dropdown), separated from the
  // fetch trigger.
  useEffect(() => {
    if (!showTaskAssignmentDropdown) return;
    if (staffLoaded) return;
    let cancelled = false;
    (async () => {
      setLoadingStaff(true);
      try {
        const response = await userApi.getStaffUsers();
        if (!cancelled) {
          setStaffUsers(response?.users || []);
          setStaffLoaded(true);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('GuestRequestModal: failed to load staff users', err);
          setStaffLoaded(true);  // mark loaded so we don't infinite-retry; falls back to "no users" UX
        }
      } finally {
        if (!cancelled) setLoadingStaff(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTaskAssignmentDropdown, staffLoaded]);

  // Phase 3.7a — clear taskAssignedTo when the conditional dropdown hides.
  // Prevents a stale value from being sent if ops picked a user, then changed
  // fulfillmentType or unticked requiresFieldTask.
  useEffect(() => {
    if (!showTaskAssignmentDropdown && formData.taskAssignedTo) {
      setFormData((prev) => ({ ...prev, taskAssignedTo: '' }));
    }
  }, [showTaskAssignmentDropdown, formData.taskAssignedTo]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.type) {
      setError('نوع الطلب مطلوب');
      return;
    }
    if (!booking?._id) {
      setError('بيانات الحجز غير متوفرة');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        bookingId: booking._id,
        type: formData.type,
        description: formData.description,
        priority: formData.priority,
        fulfillmentType: formData.fulfillmentType,
        requiresFieldTask: formData.requiresFieldTask,
        assignedTo: formData.assignedTo
      };

      // v1.1.16-D — only attach when ops set a date. Empty string omits the
      // field so the backend's null-default applies (matches taskAssignedTo
      // omit-on-empty pattern below).
      if (formData.requestedForDate) {
        payload.requestedForDate = formData.requestedForDate;
      }

      // Phase 3.7a — only attach taskAssignedTo when the conditional dropdown
      // applies AND ops actually picked someone. Backend ignores the field
      // otherwise; explicit omission keeps the wire payload clean.
      if (showTaskAssignmentDropdown && formData.taskAssignedTo) {
        payload.taskAssignedTo = formData.taskAssignedTo;
      }

      const result = await guestRequestApi.create(payload);

      if (result?.success && result.request) {
        // When an auto-Task was created, hold the modal open ~1.5s to surface
        // the new ticketId via in-modal Alert (mirrors CreateTaskModal pattern).
        // For GR-only requests (no auto-Task), close immediately as before —
        // there's no ticketId to surface and the row update on the parent is
        // confirmation enough.
        const ticketId = result.request.linkedTaskTicketId;
        if (ticketId) {
          setLoading(false);
          setSuccessTicketId(ticketId);
          setTimeout(() => {
            onCreated(result.request);
          }, 2000);
        } else {
          onCreated(result.request);
        }
      } else {
        setError(result?.error || 'حدث خطأ في إنشاء الطلب');
        setLoading(false);
      }
    } catch (err) {
      // Backend response envelope is { success: false, error: '...' }.
      // Server hard-blocks cancelled bookings with 409; the message surfaces here.
      const backendMsg = err.response?.data?.error || err.response?.data?.message;
      setError(backendMsg || 'حدث خطأ في إنشاء الطلب');
      setLoading(false);
    }
  };

  const isCompleted = booking?.status === 'completed';
  const propertyName = booking?.property?.name || booking?.displayPropertyName || 'غير محدد';
  const guestName = booking?.guestName || booking?.guest?.name || booking?.displayGuestName || 'غير محدد';

  return (
    <Modal show={show} onHide={onHide} size="lg" centered dir="rtl" className="guest-request-modal">
      <Modal.Header closeButton>
        <Modal.Title className="w-100">طلب جديد للضيف</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* ABOVE the summary, not below it: the shared wording says «لا تعتمد على الوحدة الظاهرة
            أدناه», so the caveat has to precede the unit it qualifies. Silence here would be
            indistinguishable from "the guest is in the unit shown", and that difference is which
            apartment somebody walks to. */}
        {relocationFailed && (
          <div className="alert alert-warning py-2 px-3 mb-3" dir="rtl" role="alert">
            <i className="bi bi-exclamation-triangle" aria-hidden="true"></i> {RELOCATION_UNKNOWN_AR}
          </div>
        )}

        {/* Booking context — read-only summary */}
        <div className="grm-context">
          <div className="grm-context__row">
            <span className="grm-context__label">الضيف:</span>
            <span className="grm-context__value">{guestName}</span>
          </div>
          <div className="grm-context__row">
            <span className="grm-context__label">العقار:</span>
            <span className="grm-context__value">{propertyName}</span>
          </div>
          <div className="grm-context__row">
            <span className="grm-context__label">تاريخ الوصول:</span>
            <span className="grm-context__value">{formatDate(booking?.checkIn)}</span>
          </div>
          <div className="grm-context__row">
            <span className="grm-context__label">تاريخ المغادرة:</span>
            <span className="grm-context__value">{formatDate(booking?.checkOut)}</span>
          </div>
        </div>

        {/* Directly under the booking summary it corrects, and compact — this is one fact on a form,
            not the page-level banner. Renders nothing when the guest is where the booking says. */}
        <RelocationBanner relocation={relocation} compact className="grm-relocation" />

        {isCompleted && (
          <Alert variant="warning" className="grm-warning">
            <i className="bi bi-exclamation-triangle"></i>{' '}
            هذا الحجز مكتمل. هل تريد المتابعة بإنشاء طلب جديد؟
          </Alert>
        )}

        {error && <Alert variant="danger" className="grm-error">{error}</Alert>}

        {successTicketId && (
          <Alert variant="success" className="grm-error">
            تم إنشاء المهمة #<strong>{successTicketId}</strong> بنجاح
          </Alert>
        )}

        <Form onSubmit={handleSubmit} className="grm-form">
          <Form.Group className="mb-3">
            <Form.Label>نوع الطلب <span className="grm-required">*</span></Form.Label>
            <Form.Select
              name="type"
              value={formData.type}
              onChange={handleChange}
              required
              dir="rtl"
              style={{ textAlign: 'right' }}
              className="grm-select"
            >
              <option value="">-- اختر نوع الطلب --</option>
              {TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>الوصف</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleChange}
              maxLength={2000}
              placeholder="تفاصيل إضافية عن الطلب..."
              dir="rtl"
              style={{ textAlign: 'right' }}
              className="grm-textarea"
            />
            <Form.Text className="text-muted">
              {formData.description.length} / 2000
            </Form.Text>
          </Form.Group>

          {/* v1.1.16-D — guest's stated timing intent (intake-layer).
              Optional. When ticked + checkbox enabled, this date is copied to
              the auto-Task's scheduledDate at creation. Otherwise, persists on
              the GR for retrospective LinkTaskModal prefill. */}
          <Form.Group className="mb-3">
            <Form.Label>موعد التنفيذ المطلوب</Form.Label>
            <Form.Control
              type="date"
              name="requestedForDate"
              value={formData.requestedForDate}
              onChange={handleChange}
              className="grm-input"
            />
            <Form.Text className="text-muted">
              التاريخ الذي يطلب الضيف تنفيذ الطلب فيه (اختياري)
            </Form.Text>
          </Form.Group>

          <div className="row mb-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>الأولوية</Form.Label>
                <Form.Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  dir="rtl"
                  style={{ textAlign: 'right' }}
                  className="grm-select"
                >
                  {PRIORITY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>نوع التنفيذ</Form.Label>
                {/* Locked to 'internal' until 3rd-party fulfillment partners
                 * are onboarded. The field still submits via formData so
                 * downstream code paths (taskAssignedTo gating, backend
                 * validation) keep working unchanged. Re-enable by removing
                 * `disabled` once external partners are supported. */}
                <Form.Select
                  name="fulfillmentType"
                  value={formData.fulfillmentType}
                  onChange={handleChange}
                  dir="rtl"
                  style={{ textAlign: 'right' }}
                  className="grm-select"
                  disabled
                  aria-disabled="true"
                  title="غير متاح حاليًا — التنفيذ الداخلي فقط"
                >
                  {FULFILLMENT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  التنفيذ الداخلي فقط حاليًا
                </Form.Text>
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>المسؤول عن التنفيذ</Form.Label>
            <Form.Control
              type="text"
              name="assignedTo"
              value={formData.assignedTo}
              onChange={handleChange}
              maxLength={200}
              placeholder="اسم الشخص أو الجهة المنفذة (اختياري)"
              dir="rtl"
              style={{ textAlign: 'right' }}
              className="grm-input"
            />
            <Form.Text className="text-muted">
              يمكن إدخال اسم موظف، شركة خارجية، أو سائق
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Check
              type="checkbox"
              id="grm-requires-field-task"
              name="requiresFieldTask"
              checked={formData.requiresFieldTask}
              onChange={handleChange}
              label="إنشاء مهمة ميدانية مرتبطة بهذا الطلب"
              className="grm-checkbox"
            />
            <Form.Text className="text-muted grm-checkbox-hint">
              عند تفعيل هذا الخيار، سيتم إنشاء مهمة في نظام المهام تلقائياً وربطها بالطلب
            </Form.Text>
          </Form.Group>

          {/* Phase 3.7a — auto-Task assignment dropdown.
              Conditional: only when fulfillmentType=internal AND requiresFieldTask=true.
              Removes the two-step "create request, then go re-assign in /tasks" friction
              on the most common V1 happy path. */}
          {showTaskAssignmentDropdown && (
            <Form.Group className="mb-3 grm-task-assignee">
              <Form.Label>تعيين المهمة الميدانية إلى</Form.Label>
              <Form.Select
                name="taskAssignedTo"
                value={formData.taskAssignedTo}
                onChange={handleChange}
                disabled={loadingStaff}
                dir="rtl"
                style={{ textAlign: 'right' }}
                className="grm-select"
              >
                <option value="">
                  {loadingStaff ? 'جاري تحميل الفريق...' : '-- اختر الموظف المكلف (اختياري) --'}
                </option>
                {staffUsers.map((u) => (
                  <option key={u._id} value={u._id}>
                    {u.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                {formData.taskAssignedTo
                  ? 'سيتم إنشاء المهمة وتعيينها مباشرة للموظف المختار'
                  : 'اتركه فارغاً إذا لم يتم التعيين بعد'}
              </Form.Text>
            </Form.Group>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer dir="rtl" className="justify-content-start grm-footer">
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading || !formData.type || !!successTicketId}
          className="grm-submit"
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm grm-spinner" role="status" />
              جاري الإنشاء...
            </>
          ) : (
            <>
              <i className="bi bi-plus-circle"></i> إنشاء الطلب
            </>
          )}
        </Button>
        <Button variant="secondary" onClick={onHide} disabled={loading || !!successTicketId}>
          إلغاء
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default GuestRequestModal;
